import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Circle, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { orderService } from '../../services/api';
import { Order } from '../../types/order';
import styles from './Heatmap.module.css';

type HeatPoint = {
  lat: number;
  lng: number;
  count: number;
  entries: Array<{ protocol: string | null; address: string; houseNumber: string | null; neighborhood: string | null }>;
};

type NeighborhoodStat = {
  neighborhood: string;
  count: number;
};

const DEFAULT_CENTER: [number, number] = [-8.3592, -36.6964];

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'NAO_CONCLUIDAS', label: 'Não concluídas' },
  { value: '', label: 'Todos os status' },
  { value: 'ABERTA', label: 'Aberta' },
  { value: 'AVULSA', label: 'Avulsa' },
  { value: 'EM_ANALISE', label: 'Em Análise' },
  { value: 'EM_ANDAMENTO', label: 'Em Andamento' },
  { value: 'PENDENTE', label: 'Pendente' },
  { value: 'CONCLUIDA', label: 'Concluida' },
  { value: 'EM_ATRASO', label: 'Em Atraso' },
];

const parseInputDateAsLocal = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const formatInputDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isBetweenDates = (dateValue: string, startDate: string, endDate: string): boolean => {
  const dateOnly = dateValue.slice(0, 10);
  if (startDate && dateOnly < startDate) return false;
  if (endDate && dateOnly > endDate) return false;
  return true;
};

const getHeatColor = (ratio: number): string => {
  if (ratio >= 0.85) return '#b91c1c';
  if (ratio >= 0.65) return '#dc2626';
  if (ratio >= 0.45) return '#ea580c';
  if (ratio >= 0.25) return '#f59e0b';
  return '#facc15';
};

const MapLifecycle: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(timer);
  }, [map]);

  return null;
};

const Heatmap: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('NAO_CONCLUIDAS');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const mapPanelRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const from = new Date(today);
    from.setDate(today.getDate() - 29);

    setStartDate(formatInputDate(from));
    setEndDate(formatInputDate(today));
  }, []);

  useEffect(() => {
    orderService.getAll({ perPage: '300' })
      .then((response) => {
        setOrders(response);
      })
      .catch(() => {
        setError('Erro ao carregar ordens para o mapa de calor.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!startDate || !endDate) return;

    if (parseInputDateAsLocal(startDate) > parseInputDateAsLocal(endDate)) {
      setDateError('A data inicial nao pode ser maior que a data final.');
      return;
    }

    setDateError('');
  }, [startDate, endDate]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsMapFullscreen(document.fullscreenElement === mapPanelRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (statusFilter === 'NAO_CONCLUIDAS') {
        if (order.status === 'CONCLUIDA') return false;
      } else if (statusFilter && order.status !== statusFilter) return false;
      if (!isBetweenDates(order.createdAt, startDate, endDate)) return false;
      return true;
    });
  }, [orders, statusFilter, startDate, endDate]);

  const neighborhoodStats = useMemo<NeighborhoodStat[]>(() => {
    const counts = new Map<string, number>();

    filteredOrders.forEach((order) => {
      const key = order.neighborhood?.trim() || 'Nao informado';
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([neighborhood, count]) => ({ neighborhood, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredOrders]);

  const heatPoints = useMemo<HeatPoint[]>(() => {
    const grouped = new Map<string, HeatPoint>();

    filteredOrders.forEach((order) => {
      if (order.latitude == null || order.longitude == null) return;

      const latBucket = Number(order.latitude.toFixed(3));
      const lngBucket = Number(order.longitude.toFixed(3));
      const key = `${latBucket}:${lngBucket}`;

      if (!grouped.has(key)) {
        grouped.set(key, { lat: latBucket, lng: lngBucket, count: 0, entries: [] });
      }

      const existing = grouped.get(key);
      if (existing) {
        existing.count += 1;
        existing.entries.push({
          protocol: order.protocolNumber ?? null,
          address: order.address,
          houseNumber: order.houseNumberInFront ?? null,
          neighborhood: order.neighborhood ?? null,
        });
      }
    });

    return Array.from(grouped.values()).sort((a, b) => b.count - a.count);
  }, [filteredOrders]);

  const maxPointCount = useMemo(() => {
    return heatPoints.reduce((max, point) => Math.max(max, point.count), 0);
  }, [heatPoints]);

  const mapKey = useMemo(
    () => `heatmap-${statusFilter}-${startDate}-${endDate}`,
    [statusFilter, startDate, endDate],
  );

  const mapCenter: [number, number] = useMemo(() => {
    if (!heatPoints.length) return DEFAULT_CENTER;
    return [heatPoints[0].lat, heatPoints[0].lng];
  }, [heatPoints]);

  const ordersWithCoordinates = filteredOrders.filter(
    (order) => order.latitude != null && order.longitude != null,
  ).length;

  const topNeighborhood = neighborhoodStats[0];

  const toggleMapFullscreen = async () => {
    if (!mapPanelRef.current) return;

    try {
      if (document.fullscreenElement === mapPanelRef.current) {
        await document.exitFullscreen();
        return;
      }

      await mapPanelRef.current.requestFullscreen();
    } catch {
      setError('Nao foi possivel alternar o modo de tela cheia do mapa.');
    }
  };

  const filtersContent = (
    <div className={`${styles.filtersCard} ${isMapFullscreen ? styles.filtersCardFullscreen : ''}`}>
      <div className={styles.filterField}>
        <label>Status</label>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          {STATUS_OPTIONS.map((item) => (
            <option key={item.value || 'all'} value={item.value}>{item.label}</option>
          ))}
        </select>
      </div>

      <div className={styles.filterField}>
        <label>Data inicial</label>
        <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
      </div>

      <div className={styles.filterField}>
        <label>Data final</label>
        <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
      </div>

      <button
        type="button"
        className={styles.resetButton}
        onClick={() => {
          setStatusFilter('NAO_CONCLUIDAS');
          if (orders.length) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const from = new Date(today);
            from.setDate(today.getDate() - 29);
            setStartDate(formatInputDate(from));
            setEndDate(formatInputDate(today));
          }
        }}
      >
        Limpar filtros
      </button>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Mapa de Calor</h1>
        <p className={styles.subtitle}>Concentracao de ocorrencias por localizacao e bairro.</p>
      </div>

      <div className={styles.kpiGrid}>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Ordens filtradas</span>
          <strong className={styles.kpiValue}>{filteredOrders.length}</strong>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Com coordenadas</span>
          <strong className={styles.kpiValue}>{ordersWithCoordinates}</strong>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Bairros impactados</span>
          <strong className={styles.kpiValue}>{neighborhoodStats.length}</strong>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Maior foco</span>
          <strong className={styles.kpiValueSmall}>{topNeighborhood ? `${topNeighborhood.neighborhood} (${topNeighborhood.count})` : 'Sem dados'}</strong>
        </div>
      </div>

      {!isMapFullscreen && filtersContent}

      {dateError && <p className={styles.errorText}>{dateError}</p>}
      {error && <p className={styles.errorText}>{error}</p>}

      <div className={styles.grid}>
        <section
          ref={mapPanelRef}
          className={`${styles.mapCard} ${isMapFullscreen ? styles.mapCardFullscreen : ''}`}
        >
          <div className={styles.mapCardHeader}>
            <h2 className={styles.sectionTitle}>Calor por coordenadas</h2>
            <button
              type="button"
              className={styles.fullscreenButton}
              onClick={toggleMapFullscreen}
            >
              {isMapFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
            </button>
          </div>

          {isMapFullscreen && filtersContent}

          {loading ? (
            <p className={styles.emptyText}>Carregando mapa...</p>
          ) : dateError ? (
            <p className={styles.emptyText}>Ajuste o periodo para visualizar o mapa.</p>
          ) : (
            <div className={`${styles.mapStage} ${isMapFullscreen ? styles.mapStageFullscreen : ''}`}>
              <div className={!heatPoints.length ? styles.mapWatermark : styles.mapWatermarkHidden}>
                Nenhuma ordem com coordenadas no filtro atual.
              </div>
              <MapContainer
                key={mapKey}
                center={DEFAULT_CENTER}
                zoom={14}
                minZoom={10}
                className={styles.mapContainer}
                scrollWheelZoom={true}
                doubleClickZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapLifecycle />
                {heatPoints.map((point, i) => {
                  const ratio = maxPointCount ? point.count / maxPointCount : 0;
                  const radius = 10 + ratio * 35;
                  const color = getHeatColor(ratio);
                  const rows = point.entries.map((e) => {
                    const protocol = e.protocol ?? 'S/N';
                    const parts: string[] = [];
                    if (e.address) parts.push(e.address);
                    if (e.houseNumber) parts.push(`nº ${e.houseNumber}`);
                    if (e.neighborhood) parts.push(e.neighborhood);
                    const fullAddress = parts.length > 0 ? parts.join(', ') : 'Endereço não informado';
                    return `<tr>
                      <td style="padding:2px 8px 2px 0;font-weight:600;white-space:nowrap;vertical-align:top">${protocol}</td>
                      <td style="padding:2px 0;color:#4a5568;word-break:break-word">${fullAddress}</td>
                    </tr>`;
                  }).join('');

                  return (
                    <Circle
                      key={i}
                      center={[point.lat, point.lng]}
                      radius={radius}
                      pathOptions={{
                        color,
                        fillColor: color,
                        fillOpacity: 0.50,
                      }}
                    >
                      <Popup>
                        <div style={{ fontFamily: 'sans-serif', fontSize: 13, maxHeight: 220, overflowY: 'auto', minWidth: 220 }}>
                          <strong style={{ display: 'block', marginBottom: 8 }}>
                            {point.count} ocorrência{point.count !== 1 ? 's' : ''} neste local
                          </strong>
                          <table style={{ borderCollapse: 'collapse', width: '100%' }} dangerouslySetInnerHTML={{ __html: rows }} />
                        </div>
                      </Popup>
                    </Circle>
                  );
                })}
              </MapContainer>

              <div className={`${styles.legend} ${isMapFullscreen ? styles.legendFullscreen : ''}`}>
                <span><i className={`${styles.legendDot} ${styles.legendLow}`} /> Baixa</span>
                <span><i className={`${styles.legendDot} ${styles.legendMid}`} /> Media</span>
                <span><i className={`${styles.legendDot} ${styles.legendHigh}`} /> Alta</span>
              </div>
            </div>
          )}
        </section>

        <section className={`${styles.sideCard} ${isMapFullscreen ? styles.sideCardHidden : ''}`}>
          <h2 className={styles.sectionTitle}>Ocorrencias</h2>

          {!neighborhoodStats.length ? (
            <p className={styles.emptyText}>Sem ocorrencias para o filtro atual.</p>
          ) : (
            <ul className={styles.rankList}>
              {neighborhoodStats.slice(0, 12).map((item) => {
                const ratio = topNeighborhood ? item.count / topNeighborhood.count : 0;
                return (
                  <li key={item.neighborhood} className={styles.rankItem}>
                    <div className={styles.rankHeader}>
                      <span className={styles.rankName}>{item.neighborhood}</span>
                      <span className={styles.rankValue}>{item.count}</span>
                    </div>
                    <div className={styles.rankTrack}>
                      <div
                        className={styles.rankFill}
                        style={{
                          width: `${Math.max(8, ratio * 100)}%`,
                          backgroundColor: getHeatColor(ratio),
                        }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default Heatmap;
